<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">

      <!-- Forgot Password v1 -->
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <!-- logo -->
          <img
            style="width: 8vw !important; height: 11vh !important;"
            src="@/assets/images/logo/vuexy-logo.png"
          >
        </b-link>

        <!-- ───────────── success message ───────────── -->
        <div
          v-if="emailSent"
          class="fp-success"
        >
          <span class="fp-success__icon">
            <feather-icon
              icon="MailIcon"
              size="34"
            />
            <span class="fp-success__tick">
              <feather-icon
                icon="CheckIcon"
                size="14"
              />
            </span>
          </span>

          <h3 class="fp-success__title">
            EMAIL SENT SUCCESSFULLY
          </h3>
          <p class="fp-success__sub">
            CHECK YOUR INBOX
          </p>
          <span class="fp-success__email">{{ sentTo }}</span>

          <p class="fp-success__redirect">
            Redirecting to login in <strong>{{ countdown }}</strong>s
          </p>
          <div class="fp-success__bar">
            <span :style="{ animationDuration: `${REDIRECT_SECONDS}s` }" />
          </div>

          <b-link
            :to="{ name: 'login' }"
            class="fp-success__back"
          >
            <feather-icon icon="ChevronLeftIcon" /> Back to login
          </b-link>
        </div>

        <!-- ───────────── request form ───────────── -->
        <template v-else>
          <b-card-title class="mb-1">
            Forgot Password? 🔒
          </b-card-title>
          <b-card-text class="mb-2">
            Enter your email and we'll send you instructions to reset your password
          </b-card-text>

          <!-- form -->
          <validation-observer ref="simpleRules">
            <b-form
              class="auth-forgot-password-form mt-2"
              @submit.prevent="sendResetLink"
            >
              <!-- email -->
              <b-form-group
                label="Email"
                label-for="forgot-password-email"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-input
                    id="forgot-password-email"
                    v-model="userEmail"
                    :state="errors.length > 0 ? false:null"
                    :disabled="loading"
                    name="forgot-password-email"
                    placeholder="john@example.com"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- request error -->
              <b-alert
                variant="danger"
                class="px-1 py-50"
                :show="!!errorMessage"
              >
                {{ errorMessage }}
              </b-alert>

              <!-- submit button -->
              <b-button
                variant="primary"
                block
                type="submit"
                :disabled="loading"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                {{ loading ? 'Sending...' : 'Send reset link' }}
              </b-button>
            </b-form>
          </validation-observer>

          <b-card-text class="text-center mt-2">
            <b-link :to="{name:'login'}">
              <feather-icon icon="ChevronLeftIcon" /> Back to login
            </b-link>
          </b-card-text>
        </template>

      </b-card>
      <!-- /Forgot Password v1 -->
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
// import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BCard, BLink, BCardText, BCardTitle, BFormGroup, BFormInput, BForm, BButton, BAlert, BSpinner,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import decryptData from '@/store/auth/decryptData'

// Full URL on purpose: the app's axios base URL ends with /api/admin/, this endpoint does not.
const FORGOT_PASSWORD_URL = 'https://gulftic-system.fci.group/api/password/forgot'
// seconds the success message stays before going back to the login page
const REDIRECT_SECONDS = 10

export default {
  components: {
    // VuexyLogo,
    BCard,
    BLink,
    BCardText,
    BCardTitle,
    BFormGroup,
    BFormInput,
    BButton,
    BAlert,
    BSpinner,
    BForm,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      userEmail: '',
      // validation
      required,
      email,
      loading: false,
      emailSent: false,
      sentTo: '',
      errorMessage: '',
      REDIRECT_SECONDS,
      countdown: REDIRECT_SECONDS,
      redirectTimer: null,
    }
  },
  beforeDestroy() {
    // leaving the page early (e.g. "Back to login") must not leave a timer running
    clearInterval(this.redirectTimer)
  },
  methods: {
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          this.$router.push({ name: 'auth-reset-password-v1' })
        }
      })
    },
    async sendResetLink() {
      const valid = await this.$refs.simpleRules.validate()
      if (!valid || this.loading) return

      this.loading = true
      this.errorMessage = ''
      try {
        const address = this.userEmail.trim()
        // payload: { email }
        await axios.post(FORGOT_PASSWORD_URL, { email: address })

        // any 2xx response = success -> show the message, no verification-code step
        this.sentTo = address
        this.emailSent = true
        this.startRedirect()
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error)
      } finally {
        this.loading = false
      }
    },
    startRedirect() {
      clearInterval(this.redirectTimer)
      this.countdown = REDIRECT_SECONDS
      this.redirectTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          clearInterval(this.redirectTimer)
          this.$router.push({ name: 'login' })
        }
      }, 1000)
    },
    getErrorMessage(error) {
      const fallback = 'Something went wrong. Please try again.'
      let data = error.response && error.response.data
      if (!data) return fallback

      // body may arrive as a JSON string instead of an object
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch (e) {
          // plain text is fine to show, an HTML error page is not
          return /^\s*</.test(data) ? fallback : data
        }
      }

      // encrypted body, like the other APIs of this app
      if (data.payload) {
        try {
          data = decryptData(data.payload)
        } catch (e) {
          return fallback
        }
      }

      // prefer the field errors when the API sends them, otherwise read the whole body
      const source = data.errors && typeof data.errors === 'object' ? data.errors : data
      const messages = [...new Set(this.collectMessages(source))]

      return messages.length ? messages.join(' ') : fallback
    },
    /**
     * Pulls every human readable text out of an API error body, whatever its shape:
     *   { email: ['The selected email is invalid.'] }
     *   { message: '{"email":["The selected email is invalid."]}' }   <- JSON sent as a string
     *   'plain text'
     */
    collectMessages(value) {
      if (typeof value === 'string') {
        const text = value.trim()
        if (/^[{[]/.test(text)) {
          try {
            return this.collectMessages(JSON.parse(text))
          } catch (e) {
            return []
          }
        }
        return text ? [text] : []
      }
      if (Array.isArray(value)) {
        return value.reduce((all, item) => all.concat(this.collectMessages(item)), [])
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).reduce((all, key) => all.concat(this.collectMessages(value[key])), [])
      }
      return []
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.fp-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 0 0.5rem;
  text-align: center;
  animation: fp-rise 0.4s ease-out;

  &__icon {
    position: relative;
    display: grid;
    place-items: center;
    width: 84px;
    height: 84px;
    margin-bottom: 1.25rem;
    color: #fff;
    background: linear-gradient(135deg, #3cc4a8 0%, #13a98b 100%);
    border-radius: 50%;
    box-shadow: 0 10px 24px rgba(19, 169, 139, 0.35);
  }

  &__tick {
    position: absolute;
    right: -2px;
    bottom: 0;
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    color: #13a98b;
    background: #fff;
    border: 3px solid #13a98b;
    border-radius: 50%;
    animation: fp-pop 0.45s 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  &__title {
    margin: 0 0 0.35rem;
    font-size: 1.35rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: #13a98b;
  }

  &__sub {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    color: #6b7a90;
  }

  &__email {
    max-width: 100%;
    padding: 6px 16px;
    margin-bottom: 1.5rem;
    overflow: hidden;
    color: #0a62e8;
    font-weight: 600;
    text-overflow: ellipsis;
    background: rgba(28, 132, 242, 0.1);
    border-radius: 999px;
  }

  &__redirect {
    margin: 0 0 0.6rem;
    font-size: 0.85rem;
    color: #6b7a90;

    strong {
      color: #1d2b44;
    }
  }

  /* shrinking bar = visual 10 second countdown */
  &__bar {
    width: 100%;
    max-width: 260px;
    height: 5px;
    margin-bottom: 1.25rem;
    overflow: hidden;
    background: rgba(19, 169, 139, 0.18);
    border-radius: 999px;

    span {
      display: block;
      height: 100%;
      background: linear-gradient(90deg, #3cc4a8, #13a98b);
      border-radius: 999px;
      transform-origin: left center;
      animation: fp-countdown linear forwards;
    }
  }
}

@keyframes fp-countdown {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}

@keyframes fp-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fp-pop {
  from {
    opacity: 0;
    transform: scale(0);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
